import { useState, useEffect, useRef } from 'react';
import API from './api';

// ── Constants ──────────────────────────────────────────────────────────────────

const STAGES = [
  { key: 'metadata',   label: 'Load Metadata',   pct: [0, 15]  },
  { key: 'extracting', label: 'Extract Text',     pct: [15, 55] },
  { key: 'ai',         label: 'AI Analysis',      pct: [55, 85] },
  { key: 'pdf',        label: 'Generate PDF',     pct: [85, 100]},
];

const SEVERITY_CFG = {
  CRITICAL:    { color: '#e8001d', bg: 'rgba(232,0,29,0.08)',  border: 'rgba(232,0,29,0.25)' },
  IMPROVEMENT: { color: '#2a6fdb', bg: 'rgba(42,111,219,0.08)', border: 'rgba(42,111,219,0.25)' },
};

const TYPE_LABELS = {
  TYPO:        'Typo / Grammar',
  TERMINOLOGY: 'Terminology',
  CONFLICT:    'Conflict',
  CROSS_REF:   'Cross-Reference',
  OUTDATED:    'Outdated Regulation',
  FORMAT:      'Format',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function stageIndex(stageKey) {
  const idx = STAGES.findIndex(s => s.key === stageKey);
  return idx === -1 ? 0 : idx;
}

function groupByDocument(findings) {
  const groups = {};
  for (const f of findings) {
    const key = `${f.category}::${f.doc_id}`;
    if (!groups[key])
      groups[key] = { document: f.document, category: f.category, doc_id: f.doc_id, findings: [] };
    groups[key].findings.push(f);
  }
  return Object.values(groups);
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function QDMSAnalyzer({ setPage, onLogout }) {
  const [phase, setPhase]       = useState('idle');   // idle | running | done | error
  const [job, setJob]           = useState(null);      // full job state from backend
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);      // {stage_label, type, message, detail}
  const [showDetail, setShowDetail] = useState(false);
  const [showLog, setShowLog]   = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const pollRef                 = useRef(null);

  // Stop polling on unmount
  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/qdms-analyzer/progress/${jobId}`);
        const j = res.data;
        setJob(j);

        if (j.status === 'done') {
          clearInterval(pollRef.current);
          setResult(j.result);
          setPhase('done');
        } else if (j.status === 'error') {
          clearInterval(pollRef.current);
          setError(j.error);
          setPhase('error');
        }
      } catch (e) {
        if (e.response?.status === 401) { onLogout(); clearInterval(pollRef.current); }
      }
    }, 1500);
  };

  const handleAnalyze = async () => {
    setPhase('running');
    setJob(null);
    setResult(null);
    setError(null);
    setShowDetail(false);
    setShowLog(false);
    try {
      const res = await API.post('/qdms-analyzer/analyze');
      startPolling(res.data.job_id);
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      setError({
        stage_label: 'Starting job',
        type: 'NetworkError',
        message: e.response?.data?.detail || e.message,
        detail: null,
      });
      setPhase('error');
    }
  };

  const handleDownload = () => {
    if (!result?.report_filename) return;
    const token = localStorage.getItem('token');
    window.open(`/api/qdms-analyzer/report/${result.report_filename}?token=${token}`, '_blank');
  };

  const progress  = job?.progress ?? 0;
  const curStageKey = job?.stage ?? 'metadata';
  const curStageIdx = curStageKey === 'done' ? STAGES.length : stageIndex(curStageKey);
  const groups    = result ? groupByDocument(result.findings) : [];
  const log       = result?.extraction_log ?? [];
  const logErrors = log.filter(e => e.status !== 'ok');

  return (
    <div style={s.wrapper}>
      {/* ── Header ── */}
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
        <div style={s.headerTitle}>QDMS Quality Analyzer</div>
        <button style={s.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
        <div style={s.aiBadge}>AI-POWERED</div>
      </header>

      {/* ── How to Guide Modal ── */}
      {showGuide && (
        <div style={s.overlay} onClick={() => setShowGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>How to Use QDMS Quality Analyzer</span>
              <button style={s.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>
            <div style={s.modalBody}>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>What is QDMS Analyzer?</div>
                <div style={s.guideSectionText}>
                  QDMS Analyzer is an AI-powered document quality scanner that reads all your IMS/QDMS
                  documents simultaneously and identifies quality issues across the entire corpus.
                  It checks compliance against ISM Code, SOLAS, MARPOL, MLC 2006, and ISO 9001.
                </div>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Analysis Types</div>
                <table style={s.guideTable}>
                  <thead>
                    <tr>
                      <th style={s.guideTh}>Type</th>
                      <th style={s.guideTh}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['TYPO / GRAMMAR',      'Spelling and grammar errors found in procedures or policies'],
                      ['TERMINOLOGY',         'Same concept referred to with different names across documents'],
                      ['CONFLICT',            'Contradictory instructions or different time intervals for the same topic'],
                      ['CROSS-REFERENCE',     'Reference to a document that does not exist in the QDMS system'],
                      ['OUTDATED REGULATION', 'Superseded regulation versions (e.g. old MARPOL/SOLAS amendments)'],
                      ['FORMAT',              'Missing revision date, version number, document code, or structure issues'],
                    ].map(([type, desc]) => (
                      <tr key={type}>
                        <td style={s.guideTdCode}>{type}</td>
                        <td style={s.guideTd}>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Severity Levels</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ ...s.guideTag, background: 'rgba(232,0,29,0.1)', border: '1px solid rgba(232,0,29,0.3)', color: '#e8001d' }}>
                    <strong>CRITICAL</strong> — Regulatory risk, safety gap, or direct procedural conflict
                  </div>
                  <div style={{ ...s.guideTag, background: 'rgba(42,111,219,0.1)', border: '1px solid rgba(42,111,219,0.3)', color: '#2a6fdb' }}>
                    <strong>IMPROVEMENT</strong> — Minor inconsistency or best-practice suggestion
                  </div>
                </div>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Step-by-Step Workflow</div>
                <ol style={s.guideSteps}>
                  <li><strong>Upload IMS Documents</strong> — Go to IMS Manager and upload documents to the relevant QDMS slots (GENERAL, SHORE, or CIRCULAR LETTERS categories)</li>
                  <li><strong>Run Analysis</strong> — Click RUN ANALYSIS. The system loads metadata, extracts text from all files, sends excerpts to the AI, then generates a PDF report</li>
                  <li><strong>Review Findings</strong> — Results are grouped by document. CRITICAL findings appear first with a red badge. Each finding shows the type, description, and a concrete suggestion</li>
                  <li><strong>Download PDF</strong> — Use the ↓ PDF button to download a formatted quality report for offline review or distribution</li>
                </ol>
              </div>

              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Important Notes</div>
                <ul style={s.guideNotes}>
                  <li>Analysis takes <strong>30–90 seconds</strong> depending on the number of uploaded documents</li>
                  <li>The AI reads text excerpts from each document — scanned PDFs without OCR may yield fewer findings</li>
                  <li>Results reflect the document <em>content</em> at the time of analysis — re-run after updating documents</li>
                  <li>CRITICAL findings should be reviewed and resolved before the next ISM audit or port state control inspection</li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      )}

      <main style={s.main}>

        {/* ── Control card ── */}
        <div style={s.controlCard}>
          <div style={s.controlLeft}>
            <div style={s.controlLabel}>FULL IMS QUALITY SCAN</div>
            <div style={s.controlDesc}>
              Scans all QDMS documents simultaneously for typos, inconsistent terminology,
              conflicting procedures, missing cross-references, outdated regulations, and
              formatting issues. Takes 30–90 seconds.
            </div>
          </div>
          <button
            style={{ ...s.runBtn, opacity: phase === 'running' ? 0.5 : 1 }}
            onClick={handleAnalyze}
            disabled={phase === 'running'}
          >
            {phase === 'running' ? 'ANALYZING…' : 'RUN ANALYSIS'}
          </button>
        </div>

        {/* ── Progress panel (shown while running or after) ── */}
        {(phase === 'running' || phase === 'done' || phase === 'error') && (
          <div style={s.progressCard}>

            {/* Stage steps */}
            <div style={s.stepsRow}>
              {STAGES.map((st, idx) => {
                const done    = curStageIdx > idx || phase === 'done';
                const active  = curStageIdx === idx && phase === 'running';
                const failed  = phase === 'error' && curStageIdx === idx;
                return (
                  <div key={st.key} style={s.stepItem}>
                    <div style={{
                      ...s.stepDot,
                      background: failed ? '#e8001d'
                                : done   ? '#22c55e'
                                : active ? '#c9a84c'
                                :          '#1e2d40',
                      borderColor: failed ? '#e8001d'
                                 : done   ? '#22c55e'
                                 : active ? '#c9a84c'
                                 :          '#1e2d40',
                      boxShadow: active ? '0 0 8px rgba(201,168,76,0.6)' : 'none',
                    }}>
                      {done && !failed ? '✓' : failed ? '✕' : idx + 1}
                    </div>
                    <div style={{
                      ...s.stepLabel,
                      color: done || active ? '#e8edf4' : '#4a5a70',
                    }}>{st.label}</div>
                    {idx < STAGES.length - 1 && (
                      <div style={{
                        ...s.stepConnector,
                        background: curStageIdx > idx || phase === 'done' ? '#22c55e' : '#1e2d40',
                      }} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Percentage bar */}
            <div style={s.barTrack}>
              <div style={{
                ...s.barFill,
                width: `${progress}%`,
                background: phase === 'error' ? '#e8001d'
                           : phase === 'done'  ? '#22c55e'
                           :                    '#c9a84c',
                transition: 'width 0.4s ease',
              }} />
            </div>

            {/* Status message */}
            <div style={s.statusRow}>
              <span style={{
                color: phase === 'error' ? '#e8001d'
                     : phase === 'done'  ? '#22c55e'
                     :                    '#c9a84c',
                fontSize: 12, fontFamily: 'monospace',
              }}>
                {phase === 'error' ? '✕ FAILED'
               : phase === 'done'  ? '✓ COMPLETE'
               :                    '● RUNNING'}
              </span>
              <span style={s.statusMsg}>
                {job?.message ?? ''}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, fontFamily: 'monospace', color: '#8a9ab0' }}>
                {progress}%
              </span>
            </div>
          </div>
        )}

        {/* ── Error details ── */}
        {phase === 'error' && error && (
          <div style={s.errorCard}>
            <div style={s.errorHeader}>
              <div>
                <div style={s.errorTitle}>Analysis Failed</div>
                <div style={s.errorSubtitle}>
                  Failed at stage: <strong>{error.stage_label}</strong>
                  {' · '}Error type: <strong>{error.type}</strong>
                </div>
              </div>
              <button style={s.detailToggle}
                onClick={() => setShowDetail(v => !v)}>
                {showDetail ? 'Hide detail' : 'Show detail'}
              </button>
            </div>
            <div style={s.errorMessage}>{error.message}</div>
            {showDetail && error.detail && (
              <pre style={s.errorDetail}>{error.detail}</pre>
            )}
            <div style={s.errorHint}>
              Fix the issue above then click <strong>RUN ANALYSIS</strong> again.
            </div>
          </div>
        )}

        {/* ── Results ── */}
        {phase === 'done' && result && (
          <>
            {/* Summary strip */}
            <div style={s.summaryRow}>
              {[
                { label: 'DOCUMENTS',    value: result.documents_analyzed, color: null },
                { label: 'FILES FOUND',  value: result.files_analyzed,     color: null },
                { label: 'READABLE',     value: result.files_readable,     color: '#22c55e' },
                { label: 'CRITICAL',     value: result.summary.critical,   color: '#e8001d' },
                { label: 'IMPROVEMENT',  value: result.summary.improvement,color: '#2a6fdb' },
              ].map(({ label, value, color }) => (
                <div key={label} style={s.summaryCard}>
                  <div style={{ ...s.summaryValue, color: color || '#e8edf4' }}>{value ?? '—'}</div>
                  <div style={s.summaryLabel}>{label}</div>
                </div>
              ))}
              {result.report_filename && (
                <button style={s.downloadBtn} onClick={handleDownload}>↓ PDF</button>
              )}
            </div>

            {/* Extraction log (show if there were issues) */}
            {logErrors.length > 0 && (
              <div style={s.logCard}>
                <div style={s.logHeader} onClick={() => setShowLog(v => !v)}>
                  <span>⚠ {logErrors.length} file(s) skipped during extraction</span>
                  <span style={{ fontSize: 11, color: '#8a9ab0' }}>{showLog ? '▲ hide' : '▼ show'}</span>
                </div>
                {showLog && (
                  <div style={s.logBody}>
                    {logErrors.map((e, i) => (
                      <div key={i} style={s.logRow}>
                        <span style={{ color: e.status === 'error' ? '#e8001d' : '#c9a84c' }}>
                          {e.status === 'error' ? '✕' : '○'}
                        </span>
                        <span style={{ color: '#8a9ab0', minWidth: 200 }}>{e.file}</span>
                        <span style={{ color: '#4a5a70' }}>{e.doc}</span>
                        <span style={{ marginLeft: 'auto', color: '#6a7a90', fontFamily: 'monospace', fontSize: 11 }}>
                          {e.reason}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Warnings */}
            {result.warning && <div style={s.warningBox}>{result.warning}</div>}
            {result.pdf_error && (
              <div style={s.warningBox}>
                PDF generation failed: {result.pdf_error}
              </div>
            )}

            {/* Empty findings */}
            {groups.length === 0 && !result.warning && (
              <div style={s.emptyState}>
                No quality issues identified in the current IMS document set.
              </div>
            )}

            {/* Findings by document */}
            {groups.map((group, gi) => {
              const critN = group.findings.filter(f => f.severity === 'CRITICAL').length;
              return (
                <div key={gi} style={s.docGroup}>
                  <div style={s.docGroupHeader}>
                    <span style={s.docGroupTitle}>{group.document}</span>
                    <span style={s.docGroupMeta}>
                      {group.findings.length} finding(s)
                      {critN > 0 && <span style={s.critBadge}>{critN} CRITICAL</span>}
                    </span>
                  </div>
                  {group.findings.map((f, fi) => {
                    const cfg = SEVERITY_CFG[f.severity] || SEVERITY_CFG.IMPROVEMENT;
                    return (
                      <div key={fi} style={{ ...s.findingCard, background: cfg.bg, borderLeftColor: cfg.border }}>
                        <div style={s.findingMeta}>
                          <span style={{ ...s.severityBadge, color: cfg.color, borderColor: cfg.border }}>
                            {f.severity}
                          </span>
                          <span style={s.typeBadge}>{TYPE_LABELS[f.type] || f.type}</span>
                        </div>
                        <div style={s.findingDesc}>{f.description}</div>
                        {f.suggestion && (
                          <div style={s.findingSugg}>
                            <span style={s.suggLabel}>Suggestion: </span>{f.suggestion}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </main>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = {
  wrapper: { minHeight: '100vh', background: '#0d1b2a', color: '#e8edf4', fontFamily: 'system-ui, sans-serif' },

  header: { background: '#111d2e', borderBottom: '1px solid #1e2d40', padding: '0 40px', height: 64, display: 'flex', alignItems: 'center', gap: 20 },
  backBtn: { background: 'transparent', border: '1px solid #1e2d40', color: '#8a9ab0', padding: '6px 16px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'monospace' },
  headerTitle: { fontSize: 15, fontWeight: 700, flex: 1 },
  aiBadge: { background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '4px 12px', borderRadius: 12, fontSize: 10, letterSpacing: 1, fontFamily: 'monospace' },

  main: { padding: 40, maxWidth: 980, margin: '0 auto' },

  controlCard: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12, padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 24, marginBottom: 20 },
  controlLeft: { flex: 1 },
  controlLabel: { fontSize: 11, fontWeight: 700, letterSpacing: 2, fontFamily: 'monospace', color: '#c9a84c', marginBottom: 8 },
  controlDesc: { fontSize: 13, color: '#8a9ab0', lineHeight: 1.6 },
  runBtn: { background: '#e8001d', border: 'none', borderRadius: 8, padding: '14px 28px', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'monospace', transition: 'opacity 0.2s' },

  // Progress card
  progressCard: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12, padding: '24px 28px', marginBottom: 20 },
  stepsRow: { display: 'flex', alignItems: 'center', marginBottom: 20, gap: 0 },
  stepItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', flex: 1 },
  stepDot: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#e8edf4', border: '2px solid', transition: 'all 0.3s', marginBottom: 8, zIndex: 1 },
  stepLabel: { fontSize: 11, fontFamily: 'monospace', textAlign: 'center', letterSpacing: 0.5, transition: 'color 0.3s' },
  stepConnector: { position: 'absolute', top: 15, left: '50%', width: '100%', height: 2, transition: 'background 0.3s' },

  barTrack: { height: 6, background: '#0d1b2a', borderRadius: 3, overflow: 'hidden', marginBottom: 12 },
  barFill: { height: '100%', borderRadius: 3 },

  statusRow: { display: 'flex', alignItems: 'center', gap: 10 },
  statusMsg: { fontSize: 12, color: '#8a9ab0', fontFamily: 'monospace' },

  // Error
  errorCard: { background: 'rgba(232,0,29,0.07)', border: '1px solid rgba(232,0,29,0.25)', borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  errorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  errorTitle: { fontSize: 14, fontWeight: 700, color: '#ff6b7a', marginBottom: 4 },
  errorSubtitle: { fontSize: 12, color: '#8a9ab0' },
  detailToggle: { background: 'transparent', border: '1px solid rgba(232,0,29,0.3)', color: '#ff6b7a', padding: '4px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'monospace', whiteSpace: 'nowrap' },
  errorMessage: { fontSize: 13, color: '#e8edf4', lineHeight: 1.6, marginBottom: 10 },
  errorDetail: { background: '#0d1b2a', border: '1px solid #1e2d40', borderRadius: 8, padding: 14, fontSize: 11, color: '#8a9ab0', overflowX: 'auto', whiteSpace: 'pre-wrap', marginBottom: 10, fontFamily: 'monospace', lineHeight: 1.5 },
  errorHint: { fontSize: 12, color: '#6a7a90' },

  // Summary
  summaryRow: { display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' },
  summaryCard: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 10, padding: '14px 18px', textAlign: 'center', minWidth: 90 },
  summaryValue: { fontSize: 26, fontWeight: 800, fontFamily: 'monospace' },
  summaryLabel: { fontSize: 9, color: '#8a9ab0', letterSpacing: 1.5, marginTop: 4, fontFamily: 'monospace' },
  downloadBtn: { marginLeft: 'auto', background: 'transparent', border: '1px solid #c9a84c', color: '#c9a84c', padding: '10px 20px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', fontFamily: 'monospace' },

  // Extraction log
  logCard: { background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 10, marginBottom: 16, overflow: 'hidden' },
  logHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', fontSize: 12, color: '#c9a84c', fontFamily: 'monospace' },
  logBody: { borderTop: '1px solid rgba(201,168,76,0.15)', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 6 },
  logRow: { display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, fontFamily: 'monospace' },

  warningBox: { background: 'rgba(240,165,0,0.08)', border: '1px solid rgba(240,165,0,0.25)', borderRadius: 8, padding: '12px 16px', color: '#f0a500', fontSize: 13, marginBottom: 16 },

  emptyState: { textAlign: 'center', color: '#8a9ab0', padding: 48, fontSize: 14, background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12 },

  // Findings
  docGroup: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 12, overflow: 'hidden', marginBottom: 14 },
  docGroupHeader: { padding: '12px 18px', borderBottom: '1px solid #1e2d40', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)' },
  docGroupTitle: { fontSize: 13, fontWeight: 700 },
  docGroupMeta: { fontSize: 11, color: '#8a9ab0', display: 'flex', gap: 10, alignItems: 'center' },
  critBadge: { background: 'rgba(232,0,29,0.15)', border: '1px solid rgba(232,0,29,0.3)', color: '#e8001d', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, fontFamily: 'monospace' },
  findingCard: { padding: '12px 18px', borderLeft: '3px solid', borderBottom: '1px solid #1e2d40' },
  findingMeta: { display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' },
  severityBadge: { border: '1px solid', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, fontFamily: 'monospace' },
  typeBadge: { background: 'rgba(255,255,255,0.05)', color: '#8a9ab0', padding: '2px 8px', borderRadius: 10, fontSize: 10 },
  findingDesc: { fontSize: 13, lineHeight: 1.6, marginBottom: 5 },
  findingSugg: { fontSize: 12, color: '#8a9ab0', lineHeight: 1.5 },
  suggLabel: { fontWeight: 700, color: 'rgba(201,168,76,0.85)' },

  // Guide modal
  guideBtn: { background: 'transparent', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'monospace', letterSpacing: 0.5 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#111d2e', border: '1px solid #1e2d40', borderRadius: 14, width: '100%', maxWidth: 740, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #1e2d40', flexShrink: 0 },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#e8edf4' },
  modalClose: { background: 'transparent', border: '1px solid #1e2d40', color: '#8a9ab0', width: 30, height: 30, borderRadius: 6, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBody: { overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 },
  guideSection: {},
  guideSectionTitle: { fontSize: 12, fontWeight: 700, letterSpacing: 1.5, color: '#c9a84c', fontFamily: 'monospace', marginBottom: 10, textTransform: 'uppercase' },
  guideSectionText: { fontSize: 13, color: '#8a9ab0', lineHeight: 1.7 },
  guideTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  guideTh: { textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #1e2d40', color: '#4a5a70', fontFamily: 'monospace', fontSize: 10, letterSpacing: 1 },
  guideTdCode: { padding: '7px 10px', borderBottom: '1px solid #1a2535', color: '#c9a84c', fontFamily: 'monospace', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  guideTd: { padding: '7px 10px', borderBottom: '1px solid #1a2535', color: '#8a9ab0', lineHeight: 1.5 },
  guideTag: { padding: '8px 14px', borderRadius: 8, fontSize: 12, lineHeight: 1.5 },
  guideSteps: { margin: 0, paddingLeft: 20, color: '#8a9ab0', fontSize: 13, lineHeight: 1.9 },
  guideNotes: { margin: 0, paddingLeft: 20, color: '#8a9ab0', fontSize: 13, lineHeight: 1.9 },
};
